/**
 * This component is a resolver. It is meant to be used by page templates using
 * data from a CMS.
 *
 * You can send the data you receive from the CMS to the props of this components
 * by spreading them (like <ListOfPois {...cmsData} />)
 * and then make these props fit the final module.
 *
 * Note that propTypes are not needed in this component: the GraphQL schema
 * definition is sufficient for that matter.
 */
import React from "react"
import { graphql } from "gatsby"
import { Section, Container } from "@bw/bits"
import { ListOfPois as ListOfPoisModule } from "@bw/modules"
import useCollectionPois from "../../../hooks/useCollectionPois"
import { ListFilters } from "@bw/partials"

const ListOfPois = ({
  suptitle,
  title,
  subtitle,
  button,
  buttonPosition,
  idAttribute,
  classAttribute,
  backgroundVariant,
  isFollowedByModuleWithBackgroundVariant,
  numberOfColumns,
  orderedBy,
  category,
  poiType,
  maxItems,
  displaySearch,
  displayMap,
  displayFilters,
}) => {
  const pois = useCollectionPois({
    category: category?.slug,
    type: poiType?.slug,
    limit: maxItems,
    order: orderedBy?.value,
    displayOnMap: displayMap,
  })

  const columns = numberOfColumns.value
    ? parseInt(numberOfColumns.value)
    : undefined

  return (
    <Section
      {...{
        suptitle,
        title,
        subtitle,
        button,
        buttonPosition,
        idAttribute,
        classAttribute,
        fullWidth: true,
        backgroundVariant,
        isFollowedByModuleWithBackgroundVariant,
      }}
    >
      {displayFilters ? (
        <Container>
          <ListFilters items={pois}>
            {({ items }) => (
              <ListOfPoisModule
                map={displayMap}
                search={displaySearch}
                pois={items}
                numberOfColumns={columns}
              />
            )}
          </ListFilters>
        </Container>
      ) : (
        <ListOfPoisModule
          map={displayMap}
          search={displaySearch}
          pois={pois}
          numberOfColumns={columns}
        />
      )}
    </Section>
  )
}

/**
 * This generates a GraphQL Fragment that can be used in a page query:
 *
 * Example:
 * ... on ListOfPoisModule {
 *   ...ListOfPoisModuleFragment
 * }
 *
 * This is also what you will get in the props of your ListOfPois
 * resolver component.
 */
export const query = graphql`
  fragment ListOfPoisModuleFragment on ListOfPoisModule {
    displayFilters
    numberOfColumns {
      value
    }
    orderedBy {
      value
    }
    category {
      slug
    }
    poiType {
      slug
    }
    displaySearch
    displayMap
  }
`

export { ListOfPois }
